import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { Dictionary } from 'lodash';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ZodError, ZodIssue } from 'zod';
import { XlsxService } from '../../services/xlsx.service';
import { ZodService } from '../../services/zod.service';
import { UploadFileMultiTabComponent } from '../upload-file-multi-tab/upload-file-multi-tab.component';
import { UploadFileComponent } from '../upload-file/upload-file.component';
import { UploadedFileDetail } from '../upload-file/upload-file.type';
import {
    ConfigBaseUpload,
    ConfigBaseUploadMultiTab,
    DataBaseUpload,
    UploadConfig,
} from './upload-view-component.type';

@Component({
    selector: 'app-upload-view-component',
    templateUrl: './upload-view-component.component.html',
    styleUrls: ['./upload-view-component.component.scss'],
})
export class UploadViewComponentComponent<T> {
    @ViewChild(UploadFileComponent) uploadFileComponent!: UploadFileComponent;
    @ViewChild(UploadFileMultiTabComponent)
    uploadFileMultiTabComponent!: UploadFileMultiTabComponent;

    @Input() config!: UploadConfig<T>;
    @Input() validationError: string | null = null;
    @Input() multiple = false;

    @Output() emitData: EventEmitter<DataBaseUpload> = new EventEmitter();
    @Output() emitMultipleData: EventEmitter<DataBaseUpload[]> =
        new EventEmitter();

    public multiTabErrors: Map<string, Dictionary<ZodIssue[]>> | undefined =
        undefined;
    public multiTabResults: Map<string, any[]> | undefined = undefined;
    public file: File | undefined;
    public files: File[] = [];
    public fileDetail!: UploadedFileDetail;
    public fileDetails: UploadedFileDetail[] = [];

    constructor(
        private readonly xlsxService: XlsxService,
        private readonly zodService: ZodService
    ) {}

    public isMultiTab(): boolean {
        return 'fileTabs' in this.config;
    }

    public isSingleTab(): boolean {
        return 'file' in this.config;
    }

    public get singleTabConfig(): ConfigBaseUpload<T> {
        return this.config as ConfigBaseUpload<T>;
    }

    public get multiTabConfig(): ConfigBaseUploadMultiTab<T> {
        return this.config as ConfigBaseUploadMultiTab<T>;
    }

    public async getFile(uploadedFile: NgxFileDropEntry[]) {
        if (this.multiTabErrors) {
            this.multiTabErrors = undefined;
        }

        if (this.multiple) {
            const extractedFiles = await Promise.all(
                uploadedFile.map((f) =>
                    this.xlsxService.extractFileFromUploadedFile(f)
                )
            );
            this.files = extractedFiles;
            this.file = this.files.length > 0 ? this.files[0] : undefined;
        } else {
            this.file = await this.xlsxService.extractFileFromUploadedFile(
                uploadedFile[0]
            );
        }
    }

    public handleErrors(errors: ZodError) {
        if (this.file) {
            this.file = undefined;
        }
        if (this.multiple) {
            this.files = [];
        }

        const issues = errors.issues;
        const errorsByRow = this.zodService.getIssuesByRow(issues);
        // Wrap single-tab errors in Map with single entry for consistent structure
        this.multiTabErrors = new Map([['single', errorsByRow]]);
    }

    public handleMultiTabErrors(errors: Map<string, ZodError>) {
        if (this.file) {
            this.file = undefined;
        }
        if (this.multiple) {
            this.files = [];
        }

        this.multiTabErrors = this.zodService.getIssuesByTabAndRow(errors);
        this.multiTabResults = undefined;
    }

    public handleMultiTabResults(results: Map<string, any[]>) {
        this.multiTabResults = results;
        this.multiTabErrors = undefined;
    }

    public clearFiles(isClearedFiles: boolean) {
        if (isClearedFiles) {
            this.multiTabErrors = undefined;
            this.multiTabResults = undefined;
            this.file = undefined;
            this.files = [];
            this.fileDetails = [];
        }
    }

    public removeFileAtIndex(index: number) {
        this.files.splice(index, 1);
        this.fileDetails.splice(index, 1);
        this.file = this.files.length > 0 ? this.files[0] : undefined;
    }

    public saveFile() {
        if (this.multiple) {
            if (!this.files.length || !this.fileDetails.length) {
                return;
            }

            const data: DataBaseUpload[] = this.files.map((file, i) => ({
                file,
                details: this.fileDetails[i] || this.fileDetail,
            }));

            this.emitMultipleData.emit(data);

            if (this.isSingleTab()) {
                this.uploadFileComponent.removeFiles();
            } else if (this.isMultiTab()) {
                this.uploadFileMultiTabComponent.removeFiles();
            }

            this.files = [];
            this.fileDetails = [];
            this.file = undefined;
        } else {
            if (!this.file || !this.fileDetail) {
                return;
            }

            this.emitData.emit({
                file: this.file,
                details: this.fileDetail,
            });

            if (this.isSingleTab()) {
                this.uploadFileComponent.removeFiles();
            } else if (this.isMultiTab()) {
                this.uploadFileMultiTabComponent.removeFiles();
            }

            this.file = undefined;
        }
    }

    public getFileDetail(
        $event: Pick<UploadedFileDetail, 'name' | 'extension'>[]
    ) {
        if (this.isSingleTab()) {
            const singleConfig = this.config as ConfigBaseUpload<T>;

            if (this.multiple) {
                this.fileDetails = $event.map((detail) => ({
                    ...detail,
                    segment: singleConfig.file.segment,
                    department: singleConfig.file.department,
                }));
                this.fileDetail = this.fileDetails[0];
            } else {
                this.fileDetail = {
                    ...$event[0],
                    segment: singleConfig.file.segment,
                    department: singleConfig.file.department,
                };
            }
        } else if (this.isMultiTab()) {
            const multiConfig = this.config as ConfigBaseUploadMultiTab<T>;

            if (this.multiple) {
                this.fileDetails = $event.map((detail) => ({
                    ...detail,
                    segment: multiConfig.segment,
                    department: multiConfig.department,
                }));
                this.fileDetail = this.fileDetails[0];
            } else {
                this.fileDetail = {
                    ...$event[0],
                    segment: multiConfig.segment,
                    department: multiConfig.department,
                };
            }
        }
    }

    public getXlsxColumnName(key: string | number, tabName?: string) {
        let keyInXlsx;

        if (this.isSingleTab()) {
            const singleConfig = this.config as ConfigBaseUpload<T>;
            const mapFileKeys = singleConfig.file.mapFileKeys;
            for (const originalKey in mapFileKeys) {
                if (mapFileKeys[originalKey] === key) {
                    keyInXlsx = originalKey;
                }
            }
        } else if (this.isMultiTab() && tabName) {
            const multiConfig = this.config as ConfigBaseUploadMultiTab<T>;
            const tabConfig = multiConfig.fileTabs.find(
                (tab) => tab.tabName === tabName
            );
            if (tabConfig) {
                const mapFileKeys = tabConfig.mapFileKeys;
                for (const originalKey in mapFileKeys) {
                    if (mapFileKeys[originalKey] === key) {
                        keyInXlsx = originalKey;
                    }
                }
            }
        }

        return keyInXlsx;
    }

    public isDisabledButton() {
        if (this.multiple) {
            return (
                !this.files.length ||
                Boolean(this.multiTabErrors) ||
                this.validationError != null
            );
        }
        return (
            !this.file ||
            Boolean(this.multiTabErrors) ||
            this.validationError != null
        );
    }

    public downloadTemplateImportFile() {
        if (this.isSingleTab()) {
            const singleConfig = this.config as ConfigBaseUpload<T>;
            if (singleConfig.file.example instanceof Array) {
                this.xlsxService.downloadObjectListAsXlsxFile(
                    singleConfig.file.example,
                    'template',
                    singleConfig.file.mapFileKeys
                );
            }
        }
    }
}
